import { createApi } from "@reduxjs/toolkit/query/react";

import {
  type IDistrictMember,
  type ISendMemberInvite,
} from "@/providers/store/api/district-members/district-members.interface";
import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";


const districtMembersApi = createApi({
  reducerPath: "districtMembersApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.DistrictMembers],
  endpoints: (builder) => ({
    getDistrictMembers: builder.query<IDistrictMember[], { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/districts/${districtId}/members`,
        method: "GET",
      }),
      providesTags: [ApiTags.DistrictMembers],
      transformResponse: (result: IApiResponse<IDistrictMember[]>) => result.data ?? [],
    }),
    sendDistrictMemberInvite: builder.mutation<void, ISendMemberInvite>({
      query: ({
        email, role, districtId,
      }) => ({
        url: `/districts/${districtId}/members/invite`,
        method: "POST",
        body: {
          email,
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
    deleteDistrictMemberInvite: builder.mutation<void, { districtId: string; inviteId: string }>({
      query: ({
        districtId, inviteId,
      }) => ({
        url: `/districts/${districtId}/members/invite/${inviteId}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
    resendDistrictMemberInvite: builder.mutation<void, { districtId: string; inviteId: string }>({
      query: ({
        districtId, inviteId,
      }) => ({
        url: `/districts/${districtId}/members/invite/${inviteId}/resend`,
        method: "POST",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
    updateDistrictMemberInvite: builder.mutation<void, { districtId: string; inviteId: string; role: string }>({
      query: ({
        districtId, inviteId, role,
      }) => ({
        url: `/districts/${districtId}/members/invite/${inviteId}`,
        method: "PATCH",
        body: {
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
    deleteDistrictMember: builder.mutation<void, { districtId: string; memberId: string }>({
      query: ({
        districtId, memberId,
      }) => ({
        url: `/districts/${districtId}/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
    updateDistrictMember: builder.mutation<void, { districtId: string; memberId: string; role: string }>({
      query: ({
        districtId, memberId, role,
      }) => ({
        url: `/districts/${districtId}/members/${memberId}`,
        method: "PATCH",
        body: {
          role,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.DistrictMembers]),
    }),
  }),
});

export const {
  useGetDistrictMembersQuery,
  useSendDistrictMemberInviteMutation,
  useDeleteDistrictMemberInviteMutation,
  useUpdateDistrictMemberInviteMutation,
  useDeleteDistrictMemberMutation,
  useUpdateDistrictMemberMutation,
  useResendDistrictMemberInviteMutation,
} = districtMembersApi;
export default districtMembersApi;

